<template>
  <div class="item" :class="{ 'item--deleted': statusText }">
    <div
      class="item__image"
      :class="{
        'item__image--empty': !hasImage
      }"
      :style="{
        backgroundImage: hasImage ? 'url(' + item.data.source_url + ')' : ''
      }"
    >
      <template v-if="!hasImage">
        <v-icon color="primary" :size="iconSize" class="mb-1"
          >icon-photo-add</v-icon
        >
        <p>{{ $t("message.posts.list.noPhoto") }}</p>
      </template>
      <div class="badge badge--deleted" v-if="statusText">
        <v-chip :color="isDeleted ? 'pink' : 'teal'">
          {{ statusText }}
        </v-chip>
      </div>
      <PostListItemCategory :category-name="categoryName" />
    </div>
    <div
      class="item__details"
      :class="{ 'item__details--one-col': !showStoreLocator }"
    >
      <div class="item__details--maps">
        <div class="item__label">
          {{ $t("message.photos.list.mapsStatus") }}
        </div>
        <div class="item__send-status">
          <template v-if="isFinished">
            <v-icon class="mr-2" color="primary">icon-status-confirmed</v-icon>
            <span class="mr-1">
              {{ $t("message.photos.list.sendToFacilities", []) }}
            </span>
          </template>
          <template v-else-if="isProcessing">
            <v-icon color="primary" class="mr-2">
              icon-status-pending
            </v-icon>
            <span class="mr-1">
              {{ $t("message.posts.list.sendingInProgress") }}
            </span>
          </template>

          <button class="item__send-status-btn" @click="showFacilitiesDialog()">
            {{ facilityCountLabel }}
          </button>
        </div>
        <div class="item__statuses">
          <div>
            <div class="item__label-light">
              {{ $t("message.photos.list.sendStatus") }}
            </div>
            {{ $t(`message.photos.list.status.${item.status}`) }}
          </div>
          <div v-if="isFinished">
            <div class="item__label-light">
              {{ $t("message.photos.list.details") }}
            </div>
            <button
              v-if="item.sent_count > 0"
              class="item__status success-status"
              @click="showFacilitiesDialog(POST_BY_FACILITY_STATUS.OK)"
            >
              <v-icon>icon-status-success</v-icon>
              <span>{{
                $t("message.photos.list.successCount", [item.sent_count])
              }}</span>
            </button>
            <button
              v-if="item.failed_count > 0"
              class="item__status failed-status"
              @click="showFacilitiesDialog(POST_BY_FACILITY_STATUS.FAILED)"
            >
              <v-icon>icon-status-warning</v-icon>
              <span>{{
                $t("message.photos.list.failedCount", [item.failed_count])
              }}</span>
            </button>
          </div>
        </div>
      </div>
      <div class="item__details--sl" v-if="showStoreLocator">
        <div class="item__label">
          {{ $t("message.photos.list.slStatus") }}
        </div>
        <ui-switch
          v-model="slVisible"
          :label="$t('message.photos.list.visible')"
          color="cyan"
          @change="onVisibilityChange"
        />
      </div>
    </div>
    <div class="item__additional">
      <div class="item__date">
        <div class="item__label-light mb-1">
          {{ $t("message.photos.list.sendDate") }}
        </div>
        <span>{{ formattedDate }}</span>
      </div>

      <div class="item__actions">
        <v-tooltip
          max-width="370"
          bottom
          v-if="showDelete && !item.can_be_deleted"
        >
          <template v-slot:activator="{ on }">
            <div v-on="on">
              <v-btn disabled icon>
                <v-icon color="pink">icon-trash</v-icon>
              </v-btn>
            </div>
          </template>
          <span>{{ $t("message.photos.list.delete.tooltip") }}</span>
        </v-tooltip>
        <v-btn
          v-if="showDelete && item.can_be_deleted"
          icon
          :disabled="isDeleted || isProcessing"
          @click="onDelete"
        >
          <v-icon color="pink">icon-trash</v-icon>
        </v-btn>
      </div>
    </div>
    <PostFacilitiesDialog
      :is-open="facilityDialogVisible"
      :media-id="item.id"
      :status="chosenSendingStatus"
      :fetch-method="fetchFacilities()"
      @close="closeFacilitiesDialog"
    />
  </div>
</template>
<script>
import { pluralCount } from "@/helpers/helpers";
import moment from "moment";
import PostListItemCategory from "@/components/Posts/PostListItemCategory.vue";
import { POST_STATUS, POST_ACTION } from "@/enums/posts.enum";
import {
  POST_BY_FACILITY_STATUS,
  POSTS_CALL_TO_ACTION_TYPE
} from "@/enums/posts.enum";
import postsService from "@/services/gmbPosts";

export default {
  name: "PhotoListItem",
  components: {
    PostListItemCategory,
    PostFacilitiesDialog: () =>
      import("@/components/Posts/dialog/PostFacilitiesDialog.vue")
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    showDelete: {
      type: Boolean,
      default: false
    },
    showStoreLocator: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    slVisible: null,
    facilityDialogVisible: false,
    chosenSendingStatus: null,
    POST_BY_FACILITY_STATUS
  }),
  computed: {
    facilityCountLabel() {
      return pluralCount(
        "message.pluralForms.facilityGenitive",
        this.item.facilities_count
      );
    },
    formattedDate() {
      return moment(this.item.created_at).format("DD.MM.YYYY");
    },
    hasImage() {
      return !!this.item.data.source_url;
    },
    isDeleted() {
      return this.item && this.item.is_deleted;
    },
    isFinished() {
      return (
        this.item &&
        this.item.status &&
        (this.item.status === POST_STATUS.COMPLETED ||
          this.item.status === POST_STATUS.FINISHED_WITH_PROBLEMS)
      );
    },
    statusText() {
      if (this.isProcessing) {
        if (this.isDeleting) {
          return this.$t(`message.posts.list.deletingInProgress`);
        } else if (this.isCreating) {
          return this.$t(`message.posts.list.creatingInProgress`);
        }
      }

      if (this.isDeleted) {
        return this.$t(`message.posts.list.deleted`);
      }
      return undefined;
    },
    isProcessing() {
      return (
        this.item &&
        this.item.status &&
        this.item.status === POST_STATUS.PENDING
      );
    },
    isCreating() {
      return (
        this.item &&
        this.item.data &&
        this.item.data.action === POST_ACTION.CREATE
      );
    },
    isDeleting() {
      return (
        this.item &&
        this.item.data &&
        this.item.data.action === POST_ACTION.DELETE
      );
    },
    categoryName() {
      const category =
        this.item &&
        this.item.data &&
        this.item.data.location_association &&
        this.item.data.location_association.category;

      if (!category) return "";
      return this.$t(`message.photos.list.${category}`);
    }
  },
  created() {
    this.slVisible = this.item.visible_in_store_locator;
  },
  methods: {
    fetchFacilities() {
      return postsService.getMediaFacilities;
    },
    onVisibilityChange() {
      this.$emit("sl-visible", this.slVisible);
    },
    onDelete() {
      this.$emit("delete");
    },
    showFacilitiesDialog(status) {
      this.chosenSendingStatus = status;
      this.facilityDialogVisible = true;
    },
    closeFacilitiesDialog() {
      this.chosenSendingStatus = null;
      this.facilityDialogVisible = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.item {
  display: flex;
  align-items: center;
  width: 100%;
  @include font-size(14px);
  line-height: 17px;
  color: $dark-blue-3;
  padding: $spacing * 2.5 0;
  position: relative;
  overflow: hidden;

  &--deleted {
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: $light;
      opacity: 0.5;
    }
  }

  &:not(:last-of-type) {
    border-bottom: 1px solid $light-3;
  }

  > div ~ div {
    margin-left: $spacing * 2;
  }

  &__send-status {
    display: flex;
    align-items: center;
    margin-bottom: $spacing * 2.5;

    > span {
      line-height: 30px;
    }

    &-btn {
      font-weight: 600;
      text-decoration: underline;
      color: $primary;
    }
  }

  &__details {
    display: flex;
    border-radius: 20px;
    background: $light-4;
    align-self: stretch;
    min-width: 620px;
    max-width: 922px;
    flex: 2 1 auto;

    &--one-col {
      min-width: 400px;
    }

    > div {
      padding: $spacing * 2 $spacing * 4;
      max-width: 461px;
      width: 100%;
      flex: 1;
    }

    &--maps {
      min-width: 360px;
    }

    &--sl {
      position: relative;

      &:after {
        content: "";
        position: absolute;
        top: 16px;
        left: 0;
        bottom: 16px;
        border-right: 1px solid $light-3;
      }
    }
  }
  &__image {
    position: relative;
    width: 156px;
    min-width: 156px;
    height: 164px;
    border-radius: 20px;
    background-size: cover;
    background-position: center;
    margin-right: $spacing * 3;

    &--empty {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      background-color: $light-4;

      p {
        @include font-size(12px);
        margin-bottom: 0;
      }
    }
  }

  &__statuses {
    display: flex;
    justify-content: space-between;
  }

  &__status {
    display: flex;
    align-items: center;
    margin-bottom: $spacing;

    i.v-icon {
      color: currentColor;
      margin-right: $spacing;
    }
  }

  &__additional {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  &__date {
    max-width: max-content;
    width: 100%;
    flex: 1 1 auto;
    padding: $spacing * 2;

    > span {
      font-weight: 600;
      color: $primary;
    }
  }

  &__actions {
    justify-self: flex-end;
  }

  &__label,
  &__label-light {
    @include font-size(12px);
    line-height: 17px;
    color: $dark-blue-3;
    margin-bottom: $spacing;
  }

  &__label {
    font-weight: 700;
  }

  &__label-light {
    font-weight: 400;
    opacity: 0.6;
    white-space: nowrap;
  }

  .success-status {
    color: $cyan;
  }

  .failed-status {
    color: $pink;
  }
}
.badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;

  &--deleted {
    & > .v-chip {
      @include font-size(12px);
      color: $light;
    }
  }
}
</style>
